/**
 * @module experience/landing
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
// Core
import { Injectable, EventEmitter } from '@angular/core';

// import { SETTINGS, CHANNELS } from '@environment/settings';
// Services
import { ApiGatewayService } from '@core/public';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { GetWarrantyExchange } from '@support/api/proxies/responses/api-2911.interface';
import { WarrantyExchangeQueryParams } from '@support/api/proxies/requests/api-2911.interface';
import {
  ServiceDetails,
  PlanDetails,
} from '@experience/change-plan-services/interface/add-remove-plans-services';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';

// Constants
import {
  PLAN_SERVICES_CONSTANTS,
  CART_ITEM_STATUS,
} from '@experience/landing/common/constants/line-details.constants';
import { CartItem } from '@support/api/proxies/responses/api-cart-response.interface';
import { Line } from '@support/api/proxies/responses/api-2022.interface';
import { OrderSearchResults } from '@support/api/proxies/responses/api-2813.interface';
import { throwError as observableThrowError, of as observableOf, Observable, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { CustomerAccountLineBillingDetails } from '@support/api/proxies/responses/api-2040.interface';
@Injectable()
export class LineLandingService {
  /**
   * Flag for Assisted channel i.e. Retail & Care
   */
  // public isAssistedFlow: boolean;
  /**
   * Static modal for cancellation status pop up in retail and care
   */
  public modal: StaticModal;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Subject for the service selected
   */
  public selectedServiceSubject: Subject<ServiceDetails>;
  /**
   * Holds the value of selected services as an Observable
   */
  public selectedService$: Observable<ServiceDetails>;
  /**
   * Subject for the plan selected
   */
  public selectedPlanSubject: Subject<PlanDetails>;
  /**
   * Holds the value of selected plan as an Observable
   */
  public selectedPlan$: Observable<PlanDetails>;

  /**
   * Subject for modal open and close events
   */
  public selectedMsidin: Subject<CustomerAccountLineBillingDetails>;
  /**
   * Subject for modal open and close events
   */
  public pendingOrderSubject: Subject<OrderSearchResults[]>;
  /**
   * Observable for modal open and close events
   */
  public selectedMsidin$: Observable<CustomerAccountLineBillingDetails>;
  /**
   * Observable for modal open and close events
   */
  public pendingOrderSubject$: Observable<OrderSearchResults[]>;
  /**
   * Subject for Line Device Information API 2022
   */
  public deviceDetailsLoaded: Subject<Line>;
  /**
   * Observable for Line Device Information API 2022
   */
  public deviceDetailsLoaded$: Observable<Line>;
  /**
   * Subject for modal open and close events
   */
  public lineLandingErrors: Subject<APIError>;
  /**
   * Observable for modal open and close events
   */
  public lineLandingErrors$: Observable<APIError>;

  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGatewayService: ApiGatewayService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.selectedServiceSubject = new Subject();
    this.selectedService$ = this.selectedServiceSubject.asObservable();
    this.selectedPlanSubject = new Subject();
    this.selectedPlan$ = this.selectedPlanSubject.asObservable();
    this.pendingOrderSubject = new Subject();
    this.pendingOrderSubject$ = this.pendingOrderSubject.asObservable();
    this.selectedMsidin = new Subject();
    this.selectedMsidin$ = this.selectedMsidin.asObservable();
    this.deviceDetailsLoaded = new Subject();
    this.deviceDetailsLoaded$ = this.deviceDetailsLoaded.asObservable();
    this.lineLandingErrors = new Subject();
    this.lineLandingErrors$ = this.lineLandingErrors.asObservable();
  }
  /**
   * Function to emit the updated services
   * @param serviceData updated selected services
   * @returns void
   */
  public setSelectedItems(serviceData: ServiceDetails, planData: PlanDetails): void {
    this.selectedServiceSubject.next(serviceData);
    this.selectedPlanSubject.next(planData);
  }
  /**
   * Function to get warrenty status of msisdn
   * @returns Observable<GetWarrantyExchange>
   */
  public getWarrantyExchangeInfo(
    isCached?: boolean,
    imei?: string
  ): Observable<GetWarrantyExchange> {
    const warrantyExchangeData = this.stateStoreUtilsService.getWarrantyExchangeDetails();
    let queryParams: WarrantyExchangeQueryParams;
    if (isCached && warrantyExchangeData) {
      return observableOf(warrantyExchangeData);
    } else {
      const msisdnValue = this.stateStoreUtilsService.getMsisdn();
      queryParams = imei
        ? {
            msisdn: msisdnValue,
            imei: imei,
          }
        : {
            msisdn: msisdnValue,
            imei: this.stateStoreUtilsService.getSelectedImei(),
          };
      return this.apiGatewayService
        .getRequest(
          ASSISTED_API_MAP.API_2911_getWarrantyExchange,
          null,
          queryParams,
          null,
          isCached
        )
        .pipe(
          takeUntil(this.ngUnsubscribe),
          map((response: HttpResponse<Object>) => {
            this.stateStoreUtilsService.setWarrantyExchangeDetails(response.body);
            return response.body;
          }),
          catchError((error: APIError[]) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * Creates the cancellation modal
   * @param contentData : olds the value of contentData
   * @returns StaticModal
   */
  public createCancellationModal(contentData: ConfigData): StaticModal {
    // this.isAssistedFlow = (SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.WEB);
    // if (this.isAssistedFlow) {
    //   this.modal = {
    //     headerText: contentData.cancelledStatus,
    //     bodyText: contentData.resumeFromCancellation,
    //     disableDismiss: false,
    //     disableEsc: true,
    //     buttons: [{
    //       id: PLAN_SERVICES_CONSTANTS.BACK,
    //       type: PLAN_SERVICES_CONSTANTS.SUBMIT,
    //       name: contentData.back,
    //       class: PLAN_SERVICES_CONSTANTS.BUTTON_CLASS_SECONDARY
    //     },
    //     {
    //       id: contentData.resumefromCancel,
    //       type: PLAN_SERVICES_CONSTANTS.SUBMIT,
    //       name: contentData.resumefromCancel,
    //       class: PLAN_SERVICES_CONSTANTS.BUTTON_CLASS_PRIMARY
    //     }],
    //     toggleModalEmitter: new EventEmitter(),
    //     dismissableMask: true,
    //     closableText: contentData.clickToClose
    //   };
    // }
    // else {
    this.modal = {
      headerText: contentData.supportInfo,
      bodyText: contentData.lineCancellationDescription,
      disableDismiss: false,
      disableEsc: true,
      buttons: [
        {
          id: contentData.contactUs,
          type: PLAN_SERVICES_CONSTANTS.SUBMIT,
          name: contentData.contactUs,
          class: PLAN_SERVICES_CONSTANTS.BUTTON_CLASS_PRIMARY,
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      closableText: contentData.clickToClose,
    };
    // }
    return this.modal;
  }

  /**
   * Method to get data from cart if available
   * @param services array of services from cart data
   * @param status service is added or removed status
   * @returns CartItem filtered array
   */
  public groupServicesByType(
    services: CartItem[],
    status: string,
    isSessionPass: boolean
  ): CartItem[] {
    if (services) {
      const arrFiltered = services.filter((item: CartItem) => {
        if (isSessionPass) {
          return (
            item.statusCode === status && item.offerType.toUpperCase() === CART_ITEM_STATUS.ONDEMAND
          );
        } else {
          return (
            item.statusCode === status && item.offerType.toUpperCase() !== CART_ITEM_STATUS.ONDEMAND
          );
        }
      });
      return arrFiltered && arrFiltered.length > 0 ? arrFiltered : null;
    }
  }

  /**
   * Set the Error Message when API fails or blank response is returned.
   * @returns void
   */
  public setErrorMessage(message: string): void {
    const errorObject = {
      userMessage: message,
    };
    this.lineLandingErrors.next(errorObject);
  }
}
